import { useCallback, useRef, useState } from "react";
import * as tus from "tus-js-client";
import { api, errMsg } from "@/lib/api";

const MAX_CONCURRENT = 2;

export function useUploadQueue(onVideoAdded) {
  const [jobs, setJobs] = useState([]);
  const queue = useRef([]);
  const running = useRef(0);

  const patch = (id, changes) => setJobs((all) => all.map((j) => (j.id === id ? { ...j, ...changes } : j)));

  const run = useCallback(
    async (job) => {
      running.current += 1;
      patch(job.id, { state: "starting" });
      try {
        const { data: s } = await api.post("/admin/videos/upload-session", { title: job.file.name.replace(/\.[^.]+$/, "") });
        patch(job.id, { state: "uploading", videoId: s.video_id });
        await new Promise((resolve, reject) => {
          const upload = new tus.Upload(job.file, {
            endpoint: "https://video.bunnycdn.com/tusupload",
            headers: {
              AuthorizationSignature: s.signature,
              AuthorizationExpire: String(s.expiration_time),
              VideoId: s.bunny_video_id,
              LibraryId: String(s.library_id),
            },
            metadata: { filetype: job.file.type, title: job.file.name },
            retryDelays: [0, 3000, 5000, 10000, 20000],
            onProgress: (sent, total) => patch(job.id, { progress: Math.round((sent / total) * 100) }),
            onError: reject,
            onSuccess: resolve,
          });
          upload.start();
        });
        await api.post(`/admin/videos/${s.video_id}/uploaded`);
        patch(job.id, { state: "processing", progress: 100 });
        onVideoAdded?.();
      } catch (e) {
        patch(job.id, { state: "error", error: errMsg(e) });
      } finally {
        running.current -= 1;
        pump();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onVideoAdded]
  );

  const pump = useCallback(() => {
    while (running.current < MAX_CONCURRENT && queue.current.length) run(queue.current.shift());
  }, [run]);

  const addFiles = useCallback(
    (files) => {
      const added = Array.from(files || [])
        .filter((f) => f.type.startsWith("video/") || /\.(mp4|mov|m4v|mkv|webm|avi)$/i.test(f.name))
        .map((file) => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file, progress: 0, state: "queued" }));
      if (!added.length) return 0;
      setJobs((all) => [...all, ...added]);
      queue.current.push(...added);
      pump();
      onVideoAdded?.();
      return added.length;
    },
    [pump, onVideoAdded]
  );

  const clearFinished = () => setJobs((all) => all.filter((j) => !["processing", "error"].includes(j.state)));

  return { jobs, addFiles, clearFinished };
}
